import { c as _c } from "react/compiler-runtime";
import { Collapse, MantineColor, Overlay, useMantineTheme } from '@mantine/core';
import { range } from '@mantine/hooks';
import { ChildrenProps, StyleProps } from 'components';
import { ReactNode, useState } from 'react';
import { IconContext } from 'react-icons';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { fromEntries, isFunction } from 'remeda';
import { WrapIf } from 'shared/ui';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { Glass } from './Glass';
import { Sticky } from './Sticky';
export type CardCollapsableProps = {
  title?: ((opened: boolean) => ReactNode) | ReactNode;
  color?: MantineColor;
  variant?: 'card' | 'card-minimal' | 'text';
  startCollapsed?: boolean;
  opened?: boolean;
  onChange?: (opened: boolean) => void;
  classNames?: {
    root?: string;
    title?: string;
    container?: string;
  };
  disableAnimation?: boolean;
  sticky?: boolean;
  hideExpand?: boolean;
  readonlyOverlay?: boolean;
} & ChildrenProps & StyleProps;
export const CardCollapsable = t0 => {
  const $ = _c(28);
  const {
    children,
    title: _title,
    color,
    variant: t1,
    startCollapsed,
    opened: controlledOpen,
    onChange,
    classNames: classNamesRaw,
    disableAnimation,
    sticky,
    hideExpand,
    className,
    readonlyOverlay,
    debugName
  } = t0;
  const variant = t1 === undefined ? "card" : t1;
  const [openedState, setOpened] = useState(undefined);
  const opened = controlledOpen ?? openedState ?? !startCollapsed;
  let t2;
  if ($[0] !== _title || $[1] !== opened) {
    t2 = _title == null ? undefined : isFunction(_title) ? _title(opened) : _title;
    $[0] = _title;
    $[1] = opened;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const title = t2;
  let t3;
  if ($[3] !== onChange || $[4] !== opened) {
    t3 = () => {
      if (onChange) {
        onChange(!opened);
      } else {
        setOpened(!opened);
      }
    };
    $[3] = onChange;
    $[4] = opened;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const toggle = t3;
  let t4;
  if ($[6] !== readonlyOverlay) {
    t4 = readonlyOverlay ? <Overlay color="#fff" backgroundOpacity={0.5} /> : undefined;
    $[6] = readonlyOverlay;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const overlay = t4;
  const classNames = {
    ...classNamesRaw,
    title: twMerge(opened ? "rounded-t-md rounded-b-none" : "rounded-md", classNamesRaw?.title),
    container: twMerge("rounded-b-lg rounded-t-none", classNamesRaw?.container)
  };
  const themeRaw = useMantineTheme();
  let t5;
  if ($[8] !== color || $[9] !== themeRaw) {
    t5 = color ? {
      ...themeRaw,
      primaryColor: color
    } : themeRaw;
    $[8] = color;
    $[9] = themeRaw;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const theme = t5;
  const colorHtml = color ? theme.colors[color]?.[6] : undefined;
  const t6 = `color-mix(in oklch, ${colorHtml} 80%, transparent)`;
  const t7 = `color-mix(in oklch, ${colorHtml} 7%, transparent)`;
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = {
      color: t6,
      backgroundColor: t7
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `color-mix(in oklch, ${colorHtml} 3%, transparent)`;
  let t10;
  if ($[14] !== t9) {
    t10 = {
      backgroundColor: t9
    };
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = {
      title: t8,
      container: t10
    };
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const styles = t11;
  let t12;
  if ($[19] !== color || $[20] !== theme) {
    t12 = color ? {
      ...fromEntries(range(0, 10).map(i => [`--mantine-primary-color-${i}`, theme.colors[color]?.[i]])),
      "--mantine-color-primary-filled": theme.colors[color]?.[6],
      "--mantine-color-primary-filled-hover": theme.colors[color]?.[7]
    } : {};
    $[19] = color;
    $[20] = theme;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const cssVars = t12;
  const t13 = twMerge("card", classNames.root, className);
  const t14 = match(variant).with("card", "card-minimal", () => {
    const titleClassName = twMerge("flex cursor-pointer items-center gap-2 font-semibold bg-white data-[sticking=true]:shadow-sm", variant == "card" ? "justify-between rounded-md bg-gray-50 px-2 py-1 text-gray-500 hover:bg-gray-100 " : "text-gray-400", classNames?.title);
    const titleNode = <div className={`${variant == "card" ? "grow" : ""}`}>{title}</div>;
    const rightChev = !hideExpand && <div>{opened ? <HiChevronUp /> : <HiChevronDown />}</div>;
    return <IconContext value={colorHtml ? {
      color: colorHtml
    } : {}}>{sticky ? <Sticky disabled={!sticky} className={titleClassName} style={styles.title} debugName={debugName} onClick={toggle}>{titleNode}{rightChev}</Sticky> : <div className={titleClassName} style={styles.title} onClick={toggle}>{titleNode}{rightChev}</div>}</IconContext>;
  }).otherwise(_temp);
  const t15 = disableAnimation ? <div className={classNames?.container ?? ""} style={{
    display: opened ? undefined : "none",
    position: "relative",
    ...styles.container
  }}>{children}{overlay}</div> : <Collapse in={opened} keepMounted={false}><WrapIf if={!!classNames?.container} wrap={c => <div className={classNames?.container} style={{
      position: "relative",
      ...styles.container
    }}>{c}</div>}>{children}{overlay}</WrapIf></Collapse>;
  const t16 = variant == "text" && <div className={classNames?.title ?? ""} style={{
    ...styles.title
  }}><a className="font-semibold" href="#" onClick={toggle}>{title ?? <p className="text-sm">{opened ? "show more" : "show less"}</p>}</a></div>;
  let t17;
  if ($[22] !== cssVars || $[23] !== t13 || $[24] !== t14 || $[25] !== t15 || $[26] !== t16) {
    t17 = <div className={t13} style={cssVars}>{t14}{t15}{t16}</div>;
    $[22] = cssVars;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  return t17;
};
export const CardOverlay = t0 => {
  const $ = _c(10);
  const {
    children,
    title,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = twMerge("flex flex-col items-center justify-center  p-8 ", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      content: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = title && <h1 className="text-center text-2xl font-extrabold mb-2 text-balance opacity-60">{title}</h1>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== t2 || $[8] !== t3) {
    t4 = <Glass showBorder={false} frost={0.7} displace={8} classNames={t2}>{t3}{children}</Glass>;
    $[6] = children;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp() {
  return <></>;
}